import { useMutation as useConnectMutation } from '@connectrpc/connect-query'
import type { DescMessage, DescMethodUnary } from '@bufbuild/protobuf'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@magnetic/notification'

type ToastStatus = 'positive' | 'warning' | 'information'

type ResolveWithData<TData, TValue> = TValue | ((data: TData) => TValue)
type ResolveWithError<TValue> = TValue | ((error: Error) => TValue)

interface MutationToastOptions<TSuccess> {
  successTitle: ResolveWithData<TSuccess, string>
  successMessage?: string | ((data: TSuccess) => string)
  successStatus?: ResolveWithData<TSuccess, ToastStatus>
  showSuccessToast?: boolean
  errorTitle: ResolveWithError<string>
  errorMessage?: ResolveWithError<string>
  errorStatus?: ToastStatus
  invalidate?: (queryClient: QueryClient) => void | Promise<void>
  onSuccess?: (data: TSuccess) => void | Promise<void>
  onError?: (error: Error) => void | Promise<void>
}

export function useMutationWithToast<
  TSuccess = unknown,
  TInput extends DescMessage = DescMessage,
  TOutput extends DescMessage = DescMessage,
>(
  method: DescMethodUnary<TInput, TOutput>,
  options: MutationToastOptions<TSuccess>,
) {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  const resolveWithData = <TValue,>(value: ResolveWithData<TSuccess, TValue>, data: TSuccess): TValue => {
    return typeof value === 'function'
      ? (value as (input: TSuccess) => TValue)(data)
      : value
  }

  const resolveWithError = <TValue,>(value: ResolveWithError<TValue>, error: Error): TValue => {
    return typeof value === 'function'
      ? (value as (input: Error) => TValue)(error)
      : value
  }

  return useConnectMutation(method, {
    onSuccess: async (data) => {
      const typedData = data as TSuccess
      if (options.invalidate) {
        await options.invalidate(queryClient)
      }
      if (options.onSuccess) {
        await options.onSuccess(typedData)
      }
      if (options.showSuccessToast !== false) {
        addToast({
          status: options.successStatus ? resolveWithData(options.successStatus, typedData) : 'positive',
          title: resolveWithData(options.successTitle, typedData),
          children: typeof options.successMessage === 'function'
            ? options.successMessage(typedData)
            : (options.successMessage ?? ''),
        })
      }
    },
    onError: async (error) => {
      const typedError = error instanceof Error ? error : new Error(String(error))
      if (options.onError) {
        await options.onError(typedError)
      }
      addToast({
        status: options.errorStatus ?? 'warning',
        title: resolveWithError(options.errorTitle, typedError),
        children: typeof options.errorMessage === 'function'
          ? options.errorMessage(typedError)
          : (options.errorMessage ?? typedError.message),
      })
    },
  })
}
